import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { Box, Chip, Typography, IconButton, MenuItem, Select, FormControl, InputLabel, Grid, useTheme, useMediaQuery, Tooltip, CircularProgress } from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, AttachMoney as MoneyIcon, Person as PersonIcon, Receipt as ReceiptIcon, AccountBalance as BalanceIcon, CreditCard as CreditCardIcon, Savings as SavingsIcon, AutoAwesome as AutoAwesomeIcon } from '@mui/icons-material';

import PaymentService from '../services/paymentService';
import PageHeader from '../../../components/common/PageHeader';
import ResponsiveTable from '../../../components/common/ResponsiveTable';
import PaginationComponent from '../../../components/common/PaginationComponent';
import DeleteConfirmDialog from '../../../components/common/DeleteConfirmDialog';
import FormBuilder from '../../../components/common/FormBuilder';
import ItemCard from '../../../components/common/ItemCard';
import MetricsCard from '../../../components/common/MetricsCard';
import ClientSelect from '../../../components/common/ClientSelect';
import { createPaymentSchema, updatePaymentSchema, invalidateServicesCache } from '../schemas/paymentFormSchema';
import PaymentReceiptModal from '../components/PaymentReceiptModal';

const PaymentListingPage = () => {
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down('sm'));
  
  // Data states
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Filter states (restore from localStorage if available)
  const savedFilters = (() => {
    try {
      return JSON.parse(localStorage.getItem('paymentListing.filters')) || {};
    } catch { return {}; }
  })();
  const [paymentMethodFilter, setPaymentMethodFilter] = useState(savedFilters.paymentMethod || 'all');
  const [clientFilter, setClientFilter] = useState(savedFilters.client || 'all');
  /** Φίλτρο λίστας πελατών + πληρωμών: all | active | inactive */
  const [clientStatusFilter, setClientStatusFilter] = useState(savedFilters.clientStatus || 'all');
  
  // Statistics state
  const [statistics, setStatistics] = useState({
    total_deposits: 0,
    total_services_cost: 0,
    total_paid: 0,
    balance: 0
  });
  const [statisticsLoading, setStatisticsLoading] = useState(false);
  
  // Allocations state - store allocations for each payment
  const [allocationsMap, setAllocationsMap] = useState({});
  const [allocationsLoading, setAllocationsLoading] = useState({});
  
  // Pagination states
  const savedPaging = (() => {
    try {
      return JSON.parse(localStorage.getItem('paymentListing.paging')) || {};
    } catch { return {}; }
  })();
  const [currentPage, setCurrentPage] = useState(savedPaging.page || 1);
  const [itemsPerPage, setItemsPerPage] = useState(savedPaging.pageSize || 10);
  const [totalPayments, setTotalPayments] = useState(0);

  // Sorting states
  const savedSort = (() => {
    try {
      return JSON.parse(localStorage.getItem('paymentListing.sort')) || {};
    } catch { return {}; }
  })();
  const [sortField, setSortField] = useState(savedSort.field || 'payment_date');
  const [sortDirection, setSortDirection] = useState(savedSort.direction || 'desc');

  const location = useLocation();

  // Modal states
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [receiptModalOpen, setReceiptModalOpen] = useState(false);
  const [receiptPayment, setReceiptPayment] = useState(null);
  const [receiptDownloading, setReceiptDownloading] = useState(false);
  const [suggestLoadingId, setSuggestLoadingId] = useState(null);

  // Load statistics function with debouncing and deduplication
  const statisticsRequestRef = useRef(null);
  const statisticsDebounceRef = useRef(null);
  const clientFilterRef = useRef(clientFilter);
  const clientStatusFilterRef = useRef(clientStatusFilter);
  
  // Keep ref in sync with state
  useEffect(() => {
    clientFilterRef.current = clientFilter;
  }, [clientFilter]);

  useEffect(() => {
    clientStatusFilterRef.current = clientStatusFilter;
  }, [clientStatusFilter]);
  
  const loadStatistics = useCallback(async () => {
    // Cancel previous debounce
    if (statisticsDebounceRef.current) {
      clearTimeout(statisticsDebounceRef.current);
      statisticsDebounceRef.current = null;
    }

    const clientSnap = clientFilterRef.current;
    const statusSnap = clientStatusFilterRef.current;

    const executeLoad = async () => {
      if (
        clientFilterRef.current !== clientSnap ||
        clientStatusFilterRef.current !== statusSnap
      ) {
        return;
      }

      const requestKey = `${clientSnap}|${statusSnap}`;
      if (statisticsRequestRef.current && statisticsRequestRef.current !== requestKey) {
        return;
      }

      try {
        statisticsRequestRef.current = requestKey;
        setStatisticsLoading(true);
        const clientId = clientSnap !== 'all' ? parseInt(clientSnap, 10) : null;
        let clientIsActive = null;
        if (statusSnap === 'active') clientIsActive = true;
        else if (statusSnap === 'inactive') clientIsActive = false;
        const result = await PaymentService.getStatistics(clientId, clientIsActive);
        
        if (
          clientFilterRef.current === clientSnap &&
          clientStatusFilterRef.current === statusSnap &&
          result.success
        ) {
          setStatistics({
            total_deposits: 0,
            total_services_cost: 0,
            total_paid: 0,
            balance: 0,
            ...(result.data || {})
          });
        }
      } catch (err) {
        console.error('Error loading statistics:', err);
      } finally {
        if (statisticsRequestRef.current === requestKey) {
          statisticsRequestRef.current = null;
        }
        setStatisticsLoading(false);
      }
    };

    // Debounce by 300ms
    statisticsDebounceRef.current = setTimeout(() => {
      executeLoad();
    }, 300);
  }, []); // No dependencies - uses ref instead

  // Batch load allocations for multiple payments
  const loadPaymentAllocationsBatch = useCallback(async (paymentIds) => {
    if (!paymentIds || paymentIds.length === 0) return;
    
    // Filter out already loaded payments
    const paymentsToLoad = paymentIds.filter(id => 
      allocationsMap[id] === undefined && allocationsLoading[id] !== true
    );
    
    if (paymentsToLoad.length === 0) return;
    
    // Mark as loading
    setAllocationsLoading(prev => {
      const newState = { ...prev };
      paymentsToLoad.forEach(id => { newState[id] = true; });
      return newState;
    });
    
    try {
      const result = await PaymentService.getAllocationsBatch(paymentsToLoad);
      if (result.success) {
        setAllocationsMap(prev => ({
          ...prev,
          ...result.data
        }));
      }
    } catch (err) {
      console.error('Error loading allocations batch:', err);
    } finally {
      setAllocationsLoading(prev => {
        const newState = { ...prev };
        paymentsToLoad.forEach(id => { newState[id] = false; });
        return newState;
      });
    }
  }, [allocationsMap, allocationsLoading]);

  // Load allocations for a single payment (for edit modal)
  const loadPaymentAllocations = useCallback(async (paymentId) => {
    if (allocationsMap[paymentId] || allocationsLoading[paymentId]) {
      return; // Already loaded or loading
    }
    
    try {
      setAllocationsLoading(prev => ({ ...prev, [paymentId]: true }));
      const result = await PaymentService.getAllocations(paymentId);
      if (result.success) {
        setAllocationsMap(prev => ({
          ...prev,
          [paymentId]: result.data || []
        }));
      }
    } catch (err) {
      console.error(`Error loading allocations for payment ${paymentId}:`, err);
    } finally {
      setAllocationsLoading(prev => ({ ...prev, [paymentId]: false }));
    }
  }, [allocationsMap, allocationsLoading]);

  // Monotonic id: overlapping fetches (π.χ. αλλαγή sort ενώ τρέχει προηγούμενο αίτημα)
  // δεν πρέπει να αγνοούν το νεότερο — το παλιό guard με ref έκανε skip και έμενε λάθος ταξινόμηση.
  const paymentsFetchIdRef = useRef(0);
  const loadPayments = useCallback(async () => {
    const fetchId = ++paymentsFetchIdRef.current;

    try {
      setLoading(true);
      setError(null);

      const params = {
        skip: (currentPage - 1) * itemsPerPage,
        limit: itemsPerPage
      };

      if (clientFilter !== 'all') {
        params.client_id = parseInt(clientFilter);
      }

      if (paymentMethodFilter !== 'all') {
        params.payment_method = paymentMethodFilter;
      }

      if (clientStatusFilter === 'active') {
        params.client_is_active = true;
      } else if (clientStatusFilter === 'inactive') {
        params.client_is_active = false;
      }

      params.sort_by = sortField;
      params.sort_order = sortDirection;

      const result = await PaymentService.getAll(params);

      if (fetchId !== paymentsFetchIdRef.current) {
        return;
      }

      if (result.success) {
        setPayments(result.data || []);
        setTotalPayments(result.total || result.data?.length || 0);
      } else {
        setError(result.error);
        setPayments([]);
        setTotalPayments(0);
      }
    } catch (err) {
      if (fetchId === paymentsFetchIdRef.current) {
        console.error('Error loading payments:', err);
        setError('Σφάλμα φόρτωσης πληρωμών');
        setPayments([]);
        setTotalPayments(0);
      }
    } finally {
      if (fetchId === paymentsFetchIdRef.current) {
        setLoading(false);
      }
    }
  }, [currentPage, itemsPerPage, clientFilter, paymentMethodFilter, clientStatusFilter, sortField, sortDirection]);

  // Batch load allocations for all payments when payments change
  useEffect(() => {
    if (payments.length > 0) {
      const paymentIds = payments.map(p => p.id);
      loadPaymentAllocationsBatch(paymentIds);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payments]);

  // Persist filters, paging, sorting to localStorage
  useEffect(() => {
    localStorage.setItem('paymentListing.filters', JSON.stringify({
      paymentMethod: paymentMethodFilter,
      client: clientFilter,
      clientStatus: clientStatusFilter
    }));
  }, [paymentMethodFilter, clientFilter, clientStatusFilter]);

  useEffect(() => {
    localStorage.setItem('paymentListing.paging', JSON.stringify({
      page: currentPage,
      pageSize: itemsPerPage
    }));
  }, [currentPage, itemsPerPage]);

  useEffect(() => {
    localStorage.setItem('paymentListing.sort', JSON.stringify({
      field: sortField,
      direction: sortDirection
    }));
  }, [sortField, sortDirection]);

  // Reset page and reload data when filters change
  useEffect(() => {
    setCurrentPage(1);
    // Clear allocations map when filters change
    setAllocationsMap({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientFilter, paymentMethodFilter, clientStatusFilter]);

  // Reload payments when filters or pagination change
  useEffect(() => {
    loadPayments();
    loadStatistics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, itemsPerPage, clientFilter, paymentMethodFilter, clientStatusFilter, sortField, sortDirection]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const clientParam = params.get('client');

    if (clientParam && clientParam !== clientFilter) {
      setClientFilter(clientParam);
      // Reset other filters to defaults when navigating via query
      setPaymentMethodFilter('all');
    }
  }, [location.search]);

  // Handle actions
  const handleSort = (field, direction) => {
    setSortField(field);
    setSortDirection(direction);
    setCurrentPage(1); // Reset to first page when sorting
  };

  const handleCreate = () => {
    setSelectedPayment(null);
    setCreateModalOpen(true);
  };

  const handleEdit = async (payment) => {
    try {
      // Load allocations for this payment
      const allocationsResult = await PaymentService.getAllocations(payment.id);
      if (allocationsResult.success) {
        payment.allocations = allocationsResult.data || [];
        // Store original allocations separately for balance calculation in edit form
        payment._originalAllocations = JSON.parse(JSON.stringify(allocationsResult.data || []));
      } else {
        payment.allocations = [];
        payment._originalAllocations = [];
      }
    } catch (error) {
      console.error('Error loading allocations:', error);
      payment.allocations = [];
      payment._originalAllocations = [];
    }
    
    setSelectedPayment(payment);
    setEditModalOpen(true);
  };

  /** Πρόταση κατανομής από τη λίστα: κλήση API + άνοιγμα επεξεργασίας με γραμμές (επιβεβαίωση αποθήκευσης από το χρήστη). */
  const handleSuggestFromListing = useCallback(async (payment) => {
    if (!payment?.client_id) return;
    setError(null);
    setSuggestLoadingId(payment.id);
    try {
      const sug = await PaymentService.suggestAllocations({
        clientId: payment.client_id,
        amount: parseFloat(payment.amount),
        paymentDate: payment.payment_date,
        excludePaymentId: payment.id,
      });
      if (!sug.success) {
        setError(sug.error || 'Αποτυχία πρότασης');
        return;
      }
      if (!sug.allocations?.length) {
        setError('Δεν βρέθηκε πρόταση· ίσως δεν υπάρχει ανοιχτό υπόλοιπο υπηρεσίας για αυτόν τον πελάτη.');
        return;
      }
      const allocRes = await PaymentService.getAllocations(payment.id);
      const p = { ...payment };
      p.allocations = sug.allocations.map((a) => ({
        service_id: String(a.service_id),
        allocated_amount: parseFloat(a.allocated_amount) || 0,
        notes: a.notes || '',
      }));
      p._originalAllocations = JSON.parse(JSON.stringify(allocRes.success ? allocRes.data || [] : []));
      setSelectedPayment(p);
      setEditModalOpen(true);
    } catch (e) {
      console.error(e);
      setError('Σφάλμα πρότασης κατανομής');
    } finally {
      setSuggestLoadingId(null);
    }
  }, []);

  const handleDelete = (payment) => {
    setSelectedPayment(payment);
    setDeleteDialogOpen(true);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
  };

  // Form submission handlers
  const handleCreateSubmit = async (formData) => {
    try {
      // Clean up form data - remove empty strings and convert to proper types
      const cleanedData = {
        ...formData,
        // Convert client_id to integer (required field)
        client_id: parseInt(formData.client_id),
        // Convert empty strings to null for optional fields
        invoice_id: formData.invoice_id || null,
        reference_number: formData.reference_number || null,
        notes: formData.notes || null,
        // Ensure amount is a number
        amount: parseFloat(formData.amount) || 0,
        // Ensure allocations array is properly formatted
        allocations: formData.allocations && formData.allocations.length > 0 
          ? formData.allocations.map(alloc => ({
              service_id: parseInt(alloc.service_id),
              allocated_amount: parseFloat(alloc.allocated_amount),
              notes: alloc.notes || null
            }))
          : []
      };
      
      const result = await PaymentService.create(cleanedData);
      if (result.success) {
        setCreateModalOpen(false);
        await loadPayments(); // Wait for payments to reload
        const created = result.data;
        if (created?.id && Array.isArray(created.allocations)) {
          setAllocationsMap((prev) => ({
            ...prev,
            [created.id]: created.allocations
          }));
        }

        // Invalidate services cache for this client to force refresh of dropdown options
        if (cleanedData.client_id) {
          invalidateServicesCache(cleanedData.client_id);
        }
        
        // Reload statistics after payment update (with a small delay to ensure backend has updated)
        setTimeout(() => {
          loadStatistics();
        }, 500);
        return { success: true };
      } else {
        console.error('Payment creation failed:', result.error);
        return { success: false, error: result.error };
      }
    } catch (error) {
      console.error('Payment creation error:', error);
      return { success: false, error: 'Σφάλμα δημιουργίας πληρωμής' };
    }
  };

  const handleEditSubmit = async (formData) => {
    try {
      const mappedAllocations = (formData.allocations || [])
        .filter(
          (alloc) =>
            alloc.service_id &&
            alloc.service_id !== '' &&
            alloc.allocated_amount !== '' &&
            alloc.allocated_amount != null &&
            parseFloat(alloc.allocated_amount) > 0
        )
        .map((alloc) => ({
          service_id: parseInt(alloc.service_id, 10),
          allocated_amount: parseFloat(alloc.allocated_amount),
          notes: alloc.notes || null
        }));

      // Clean up form data - remove empty strings and convert to proper types
      const cleanedData = {
        ...formData,
        client_id: parseInt(formData.client_id, 10),
        invoice_id: formData.invoice_id || null,
        reference_number: formData.reference_number || null,
        notes: formData.notes || null,
        amount: parseFloat(formData.amount) || 0
      };

      delete cleanedData.allocations;
      delete cleanedData._originalAllocations;
      delete cleanedData._refresh;

      // Ρητή εντολή αντικατάστασης κατανομών (το API αγνοεί allocations χωρίς replace_allocations: true).
      cleanedData.allocations = mappedAllocations;
      cleanedData.replace_allocations = true;

      const result = await PaymentService.update(selectedPayment.id, cleanedData);
      if (result.success) {
        // Don't close the modal automatically - let user decide when to close
        await loadPayments(); // Wait for payments to reload
        // Reload allocations for this payment
        if (selectedPayment.id) {
          const allocationsResult = await PaymentService.getAllocations(selectedPayment.id);
          if (allocationsResult.success) {
            setAllocationsMap(prev => ({
              ...prev,
              [selectedPayment.id]: allocationsResult.data || []
            }));
          }
        }
        // Invalidate services cache for this client to force refresh of dropdown options
        if (selectedPayment.client_id) {
          invalidateServicesCache(selectedPayment.client_id);
        }
        // Reload statistics after payment update (with a small delay to ensure backend has updated)
        setTimeout(() => {
          loadStatistics();
        }, 500);
        return { success: true };
      } else {
        return { success: false, error: result.error };
      }
    } catch (error) {
      return { success: false, error: 'Σφάλμα ενημέρωσης πληρωμής' };
    }
  };

  const handleDeleteConfirm = async () => {
    try {
      const result = await PaymentService.delete(selectedPayment.id);
      if (result.success) {
        setDeleteDialogOpen(false);
        setSelectedPayment(null);
        await loadPayments(); // Wait for payments to reload
        // Invalidate services cache for this client to force refresh of dropdown options
        if (selectedPayment.client_id) {
          invalidateServicesCache(selectedPayment.client_id);
        }
        // Reload statistics after payment update (with a small delay to ensure backend has updated)
        setTimeout(() => {
          loadStatistics();
        }, 500);
      } else {
        setError(result.error);
      }
    } catch (error) {
      setError('Σφάλμα διαγραφής πληρωμής');
    }
  };

  // Helper to calculate allocation summary for a payment
  const getPaymentAllocationSummary = (payment) => {
    const allocations = allocationsMap[payment.id] || [];
    // Η λίστα επιστρέφει distributed/remaining από τη DB (ίδια με server-side sort)·
    // χωρίς αυτό, μέχρι να φορτώσει το batch allocations όλα φαίνονται 0 / πλήρες ποσό.
    const hasListTotals =
      payment.distributed_amount !== undefined &&
      payment.distributed_amount !== null &&
      payment.remaining_amount !== undefined &&
      payment.remaining_amount !== null;

    let distributedAmount;
    let remainingAmount;
    if (hasListTotals) {
      distributedAmount = parseFloat(payment.distributed_amount) || 0;
      const rawRem = parseFloat(payment.remaining_amount) || 0;
      const roundedRemaining = Math.round(rawRem * 100) / 100;
      remainingAmount = Math.abs(roundedRemaining) < 0.01 ? 0 : roundedRemaining;
    } else {
      distributedAmount = allocations.reduce(
        (sum, alloc) => sum + (parseFloat(alloc.allocated_amount) || 0),
        0
      );
      const rawRemainingAmount = (parseFloat(payment.amount) || 0) - distributedAmount;
      const roundedRemaining = Math.round(rawRemainingAmount * 100) / 100;
      remainingAmount = Math.abs(roundedRemaining) < 0.01 ? 0 : roundedRemaining;
    }
    
    const services = allocations.map(alloc => ({
      name: alloc.service_name || `Υπηρεσία ${alloc.service_id}`,
      amount: parseFloat(alloc.allocated_amount) || 0
    }));
    
    return {
      distributedAmount,
      remainingAmount,
      services
    };
  };

  // Mobile card renderer
  const renderPaymentCard = (payment) => {
    const summary = getPaymentAllocationSummary(payment);
    const showSuggest = payment.client_id && summary.remainingAmount > 0.01;

    return (
      <ItemCard
        key={payment.id}
        title={PaymentService.formatAmount(payment.amount)}
        subtitle={payment.client_name || 'Άγνωστος πελάτης'}
        headerActions={[]}
        basicInfo={[
          { icon: <MoneyIcon fontSize="small" />, text: PaymentService.formatAmount(payment.amount) },
          { icon: <PersonIcon fontSize="small" />, text: payment.client_name || 'Άγνωστος πελάτης' },
          { icon: <ReceiptIcon fontSize="small" />, text: PaymentService.formatDate(payment.payment_date) },
          { icon: '💰', text: `Κατανεμημένο: ${PaymentService.formatAmount(summary.distributedAmount)}` },
          { icon: '💵', text: `Μη κατανεμημένο: ${PaymentService.formatAmount(summary.remainingAmount)}` },
          summary.services.length > 0 && {
            icon: '🔧',
            text: `Υπηρεσίες: ${summary.services.map(s => s.name).join(', ')}`
          }
        ].filter(info => info.text)}
        actions={[
          <IconButton
            key="receipt"
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              handleReceipt(payment);
            }}
            sx={{ color: 'secondary.main' }}
          >
            <ReceiptIcon fontSize="small" />
          </IconButton>,
          showSuggest && (
            <Tooltip key="suggest" title="Πρόταση κατανομής">
              <span>
                <IconButton
                  size="small"
                  disabled={suggestLoadingId === payment.id}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleSuggestFromListing(payment);
                  }}
                  sx={{ color: 'secondary.dark' }}
                >
                  {suggestLoadingId === payment.id ? (
                    <CircularProgress size={18} />
                  ) : (
                    <AutoAwesomeIcon fontSize="small" />
                  )}
                </IconButton>
              </span>
            </Tooltip>
          ),
          <IconButton
            key="edit"
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              handleEdit(payment);
            }}
            sx={{ color: 'primary.main' }}
          >
            <EditIcon fontSize="small" />
          </IconButton>,
          <IconButton
            key="delete"
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              handleDelete(payment);
            }}
            sx={{ color: 'error.main' }}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        ]}
      />
    );
  };

  // Table columns configuration
  const columns = [
    {
      field: 'amount',
      headerName: 'Ποσό',
      sortable: true,
      minWidth: 120,
      renderCell: (value) => (
        <Typography variant="body2" fontWeight={600} color="primary">
          {PaymentService.formatAmount(value)}
        </Typography>
      )
    },
    {
      field: 'client_name',
      headerName: 'Πελάτης',
      sortable: true,
      minWidth: 200,
      renderCell: (value) => (
        <Typography variant="body2" fontWeight={500}>
          {value || 'Άγνωστος πελάτης'}
        </Typography>
      )
    },
    {
      field: 'payment_date',
      headerName: 'Ημερομηνία',
      sortable: true,
      minWidth: 120,
      renderCell: (value) => PaymentService.formatDate(value)
    },
    {
      field: 'distributed_amount',
      headerName: 'Κατανεμημένο',
      sortable: true,
      minWidth: 150,
      renderCell: (value, row) => {
        const summary = getPaymentAllocationSummary(row);
        return (
          <Typography variant="body2" color={summary.distributedAmount > 0 ? 'success.main' : 'text.secondary'}>
            {PaymentService.formatAmount(summary.distributedAmount)}
          </Typography>
        );
      }
    },
    {
      field: 'remaining_amount',
      headerName: 'Μη κατανεμημένο',
      sortable: true,
      minWidth: 150,
      renderCell: (value, row) => {
        const summary = getPaymentAllocationSummary(row);
        const color = summary.remainingAmount > 0 ? 'warning.main' : summary.remainingAmount < 0 ? 'error.main' : 'text.secondary';
        return (
          <Typography variant="body2" fontWeight={summary.remainingAmount !== 0 ? 600 : 400} color={color}>
            {PaymentService.formatAmount(summary.remainingAmount)}
          </Typography>
        );
      }
    },
    {
      field: 'services',
      headerName: 'Υπηρεσίες',
      sortable: true,
      minWidth: 250,
      renderCell: (value, row) => {
        const summary = getPaymentAllocationSummary(row);
        if (summary.services.length === 0) {
          return <Typography variant="body2" color="text.secondary">-</Typography>;
        }
        return (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {summary.services.map((service, idx) => (
              <Chip
                key={idx}
                label={`${service.name}: ${PaymentService.formatAmount(service.amount)}`}
                size="small"
                variant="outlined"
                color="primary"
              />
            ))}
          </Box>
        );
      }
    },
    {
      field: 'actions',
      headerName: 'Ενέργειες',
      sortable: false,
      minWidth: 168,
      renderCell: (value, row) => {
        const summary = getPaymentAllocationSummary(row);
        const showSuggest =
          row.client_id && summary.remainingAmount > 0.01;
        return (
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'nowrap' }}>
            <IconButton
              size="small"
              onClick={() => handleReceipt(row)}
              sx={{ color: 'secondary.main' }}
            >
              <ReceiptIcon fontSize="small" />
            </IconButton>
            {showSuggest && (
              <Tooltip title="Πρόταση κατανομής (ανοίγει επεξεργασία με προτεινόμενες γραμμές)">
                <span>
                  <IconButton
                    size="small"
                    disabled={suggestLoadingId === row.id}
                    onClick={() => handleSuggestFromListing(row)}
                    sx={{ color: 'secondary.dark' }}
                  >
                    {suggestLoadingId === row.id ? (
                      <CircularProgress size={18} />
                    ) : (
                      <AutoAwesomeIcon fontSize="small" />
                    )}
                  </IconButton>
                </span>
              </Tooltip>
            )}
            <IconButton
              size="small"
              onClick={() => handleEdit(row)}
              sx={{ color: 'primary.main' }}
            >
              <EditIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              onClick={() => handleDelete(row)}
              sx={{ color: 'error.main' }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Box>
        );
      }
    }
  ];

  // Custom filters - render directly instead of as a component to avoid re-mounting
  const filtersContent = (
    <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
      <FormControl size="small" sx={{ minWidth: 200 }}>
        <InputLabel>Κατάσταση</InputLabel>
        <Select
          value={clientStatusFilter}
          label="Κατάσταση"
          onChange={(e) => setClientStatusFilter(e.target.value)}
        >
          <MenuItem value="all">Όλοι</MenuItem>
          <MenuItem value="active">Ενεργοί</MenuItem>
          <MenuItem value="inactive">Ανενεργοί</MenuItem>
        </Select>
      </FormControl>
      <Box sx={{ minWidth: 250 }}>
        <ClientSelect
          value={clientFilter !== 'all' ? clientFilter : ''}
          onChange={(selectedId) => {
            setClientFilter(selectedId || 'all');
          }}
          label="Πελάτης"
          placeholder="Αναζήτηση πελάτη..."
          size="small"
          clientListMode={clientStatusFilter}
        />
      </Box>
      
      <FormControl size="small" sx={{ minWidth: 150 }}>
        <InputLabel>Τρόπος Πληρωμής</InputLabel>
        <Select
          value={paymentMethodFilter}
          label="Τρόπος Πληρωμής"
          onChange={(e) => setPaymentMethodFilter(e.target.value)}
        >
          <MenuItem value="all">Όλοι</MenuItem>
          <MenuItem value="cash">Μετρητά</MenuItem>
          <MenuItem value="bank_transfer">Κατάθεση</MenuItem>
          <MenuItem value="card">Κάρτα</MenuItem>
          <MenuItem value="check">Επιταγή</MenuItem>
          <MenuItem value="other">Άλλο</MenuItem>
        </Select>
      </FormControl>
    </Box>
  );

  // Page header actions
  const pageActions = [
    <IconButton
      key="create"
      color="primary"
      onClick={handleCreate}
      sx={{ 
        backgroundColor: 'primary.main',
        color: 'white',
        '&:hover': {
          backgroundColor: 'primary.dark',
        }
      }}
    >
      <AddIcon />
    </IconButton>
  ];

  const handleReceipt = async (payment) => {
    if (!payment) return;
    setReceiptPayment(payment);
    await loadPaymentAllocations(payment.id);
    setReceiptModalOpen(true);
  };

  const handleReceiptClose = () => {
    setReceiptModalOpen(false);
    setReceiptPayment(null);
  };

  const handleDownloadReceipt = async () => {
    if (!receiptPayment) return;
    try {
      setReceiptDownloading(true);
      const result = await PaymentService.downloadReceipt(receiptPayment.id);
      if (result.success) {
        const blob = new Blob([result.data], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = result.filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } else {
        setError(result.error);
      }
    } catch (err) {
      console.error('Error downloading receipt:', err);
      setError('Σφάλμα λήψης απόδειξης');
    } finally {
      setReceiptDownloading(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Page Header */}
      <PageHeader
        title="Διαχείριση Πληρωμών"
        subtitle="Προβολή και διαχείριση όλων των πληρωμών"
        icon="💰"
        actions={pageActions}
      />

      {/* Custom Filters */}
      {filtersContent}

      {/* Στατιστικά: ίδια λογική με client listing (πιστωτικό / εξοφλημένο / υπόλοιπο) + καταθέσεις */}
      <Grid container spacing={isXs ? 1 : 2} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={6} md={3}>
          <MetricsCard
            title="Συνολικό Ποσό Καταθέσεων"
            value={PaymentService.formatAmount(statistics.total_deposits)}
            icon={<CreditCardIcon />}
            color="success"
            loading={statisticsLoading}
            formatNumber={false}
          />
        </Grid>
        <Grid item xs={6} sm={6} md={3}>
          <MetricsCard
            title="Συνολικό Πιστωτικό"
            value={PaymentService.formatAmount(statistics.total_services_cost)}
            icon={<MoneyIcon />}
            color="info"
            loading={statisticsLoading}
            formatNumber={false}
          />
        </Grid>
        <Grid item xs={6} sm={6} md={3}>
          <MetricsCard
            title="Εξοφλημένο"
            value={PaymentService.formatAmount(statistics.total_paid)}
            icon={<SavingsIcon />}
            color="success"
            loading={statisticsLoading}
            formatNumber={false}
          />
        </Grid>
        <Grid item xs={6} sm={6} md={3}>
          <MetricsCard
            title="Υπόλοιπο"
            value={PaymentService.formatAmount(statistics.balance)}
            icon={<BalanceIcon />}
            color={Number(statistics.balance) >= 0 ? 'warning' : 'error'}
            loading={statisticsLoading}
            formatNumber={false}
          />
        </Grid>
      </Grid>

      {/* Error Display */}
      {error && (
        <Box sx={{ mb: 2 }}>
          <Typography color="error">
            {typeof error === 'string' ? error : JSON.stringify(error)}
          </Typography>
        </Box>
      )}

      {/* Data Table */}
      <ResponsiveTable
        data={payments}
        columns={columns}
        loading={loading}
        emptyMessage="Δεν βρέθηκαν πληρωμές"
        sortField={sortField}
        sortDirection={sortDirection}
        onSort={handleSort}
        renderMobileCard={renderPaymentCard}
      />

      {/* Pagination */}
      <PaginationComponent
        currentPage={currentPage}
        totalPages={Math.ceil(totalPayments / itemsPerPage)}
        totalItems={totalPayments}
        itemsPerPage={itemsPerPage}
        onPageChange={handlePageChange}
        onItemsPerPageChange={handleItemsPerPageChange}
      />

      {/* Create Modal */}
      <FormBuilder
        open={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        schema={createPaymentSchema}
        onSubmit={handleCreateSubmit}
        modalProps={{
          title: 'Δημιουργία Νέας Πληρωμής',
          maxWidth: 'md'
        }}
      />

      {/* Edit Modal */}
      <FormBuilder
        open={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        schema={updatePaymentSchema}
        entity={selectedPayment}
        onSubmit={handleEditSubmit}
        onFieldChange={(fieldName, value, formData) => {
          // Force reload of service options when allocations change
          if (fieldName === 'allocations' && formData.client_id) {
            // Invalidate cache when allocations change to force refresh of dropdown options
            invalidateServicesCache(formData.client_id);
          }
        }}
        modalProps={{
          title: 'Επεξεργασία Πληρωμής',
          maxWidth: 'md'
        }}
      />

      <PaymentReceiptModal
        open={receiptModalOpen}
        onClose={handleReceiptClose}
        payment={receiptPayment}
        summary={receiptPayment ? getPaymentAllocationSummary(receiptPayment) : null}
        onDownload={handleDownloadReceipt}
        downloading={receiptDownloading}
      />

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleDeleteConfirm}
        title="Διαγραφή Πληρωμής"
        message={`Είστε σίγουροι ότι θέλετε να διαγράψετε την πληρωμή ${selectedPayment ? PaymentService.formatAmount(selectedPayment.amount) : ''};`}
        confirmText="Διαγραφή"
        cancelText="Ακύρωση"
      />
    </Box>
  );
};

export default PaymentListingPage;